import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import userMetadataApi from '../../api/userMetadataApi';
import { useUser } from '../../context/UserContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { TutorialConfig } from './tutorialConfig';
import { useIsMobile } from '../../utils/hooks';

interface TutorialOverlayProps {
    config: TutorialConfig;
    forceOpen?: boolean;
    onClose?: () => void;
    onComplete?: () => void;
}

type HighlightRect = {
    top: number;
    left: number;
    width: number;
    height: number;
};

const fallbackRect: HighlightRect = {
    top: 120,
    left: 120,
    width: 360,
    height: 120,
};

function getVisibleClipRect(element: Element): Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'> {
    let left = 0;
    let right = window.innerWidth;
    let top = 0;
    let bottom = window.innerHeight;
    let parent = element.parentElement;

    while (parent) {
        const style = window.getComputedStyle(parent);
        const clipsX = ['auto', 'scroll', 'hidden', 'clip'].some((value) =>
            [style.overflowX, style.overflow].includes(value),
        );
        const clipsY = ['auto', 'scroll', 'hidden', 'clip'].some((value) =>
            [style.overflowY, style.overflow].includes(value),
        );
        if (clipsX || clipsY) {
            const bounds = parent.getBoundingClientRect();
            if (clipsX) {
                left = Math.max(left, bounds.left);
                right = Math.min(right, bounds.right);
            }
            if (clipsY) {
                top = Math.max(top, bounds.top);
                bottom = Math.min(bottom, bounds.bottom);
            }
        }
        parent = parent.parentElement;
    }

    return { left, right, top, bottom };
}

export default function TutorialOverlay({ config, forceOpen = false, onClose, onComplete }: TutorialOverlayProps) {
    const { userMetadata, isAdmin } = useUser();
    const { activeBusinessId } = useWorkspace();
    const queryClient = useQueryClient();
    const isMobile = useIsMobile(640);
    const completedVersion = userMetadata?.tutorial_state?.completed_pages?.[config.pageKey]?.version || 0;
    const shouldAutoOpen = !isAdmin && completedVersion < config.version;
    const [isOpen, setIsOpen] = useState(forceOpen || shouldAutoOpen);
    const [stepIndex, setStepIndex] = useState(0);
    const [rect, setRect] = useState<HighlightRect>(fallbackRect);
    const [anchorRect, setAnchorRect] = useState<HighlightRect>(fallbackRect);
    const [rectRefreshToken, setRectRefreshToken] = useState(0);

    useEffect(() => {
        setIsOpen(forceOpen || shouldAutoOpen);
        setStepIndex(0);
    }, [config.pageKey, config.version, forceOpen, shouldAutoOpen]);

    const step = config.steps[stepIndex];
    const isLastStep = stepIndex >= config.steps.length - 1;
    const shouldHighlight = step?.highlight !== false;
    const placement = step?.placement || 'bottom';
    const effectivePlacement = isMobile ? 'bottom' : placement;

    useEffect(() => {
        if (!isOpen || !step?.enterEvent) return;
        window.dispatchEvent(new CustomEvent(step.enterEvent, { detail: { pageKey: config.pageKey, stepIndex } }));
        let frameOne = 0;
        let frameTwo = 0;
        const timeoutOne = window.setTimeout(() => setRectRefreshToken((value) => value + 1), 80);
        const timeoutTwo = window.setTimeout(() => setRectRefreshToken((value) => value + 1), 180);
        frameOne = window.requestAnimationFrame(() => {
            frameTwo = window.requestAnimationFrame(() => setRectRefreshToken((value) => value + 1));
        });
        return () => {
            window.clearTimeout(timeoutOne);
            window.clearTimeout(timeoutTwo);
            window.cancelAnimationFrame(frameOne);
            window.cancelAnimationFrame(frameTwo);
        };
    }, [config.pageKey, isOpen, step?.enterEvent, stepIndex]);

    useEffect(() => {
        if (!isOpen || !step) return;
        window.dispatchEvent(new CustomEvent('finz:tutorial:step', {
            detail: {
                pageKey: config.pageKey,
                stepIndex,
                stepTitle: step.title,
            },
        }));
    }, [config.pageKey, isOpen, step, stepIndex]);

    useEffect(() => {
        if (!isOpen) return;
        const updateRect = () => {
            const targets: Element[] = step?.target
                ? (step.targetAll ? Array.from(document.querySelectorAll(step.target)) : [document.querySelector(step.target)].filter((element): element is Element => Boolean(element)))
                : [];
            const target = targets[0] || null;
            if (!target) {
                setRect(fallbackRect);
                window.setTimeout(updateRect, 150);
                return;
            }
            if (isMobile && !step?.target?.includes('data-tutorial-nav')) {
                target.scrollIntoView({ block: 'center', inline: 'center', behavior: 'auto' });
            }
            const next = targets.slice(1).reduce((acc, element) => {
                const bounds = element.getBoundingClientRect();
                const left = Math.min(acc.left, bounds.left);
                const top = Math.min(acc.top, bounds.top);
                const right = Math.max(acc.right, bounds.right);
                const bottom = Math.max(acc.bottom, bounds.bottom);
                return {
                    left,
                    top,
                    right,
                    bottom,
                    width: right - left,
                    height: bottom - top,
                    x: left,
                    y: top,
                    toJSON: acc.toJSON,
                } as DOMRect;
            }, target.getBoundingClientRect());
            const padding = typeof step?.padding === 'number' ? step.padding : 4;
            const visibleClip = isMobile ? getVisibleClipRect(target) : {
                left: 0,
                right: window.innerWidth,
                top: 0,
                bottom: window.innerHeight,
            };
            const clampedLeft = Math.max(visibleClip.left, next.left - padding);
            const clampedTop = Math.max(visibleClip.top, next.top - padding);
            const clampedRight = Math.min(visibleClip.right, next.right + padding);
            const clampedBottom = Math.min(visibleClip.bottom, next.bottom + padding);
            const highlightRect = {
                top: clampedTop,
                left: clampedLeft,
                width: Math.max(32, clampedRight - clampedLeft),
                height: Math.max(32, clampedBottom - clampedTop),
            };
            setRect(highlightRect);

            const anchorTarget = step?.anchorTarget ? document.querySelector(step.anchorTarget) : target;
            if (!anchorTarget) {
                setAnchorRect(highlightRect);
                return;
            }
            const anchor = anchorTarget.getBoundingClientRect();
            setAnchorRect({
                top: Math.max(0, anchor.top),
                left: Math.max(0, anchor.left),
                width: Math.max(32, anchor.width),
                height: Math.max(32, anchor.height),
            });
        };
        updateRect();
        window.addEventListener('resize', updateRect);
        window.addEventListener('scroll', updateRect, true);
        return () => {
            window.removeEventListener('resize', updateRect);
            window.removeEventListener('scroll', updateRect, true);
        };
    }, [isMobile, isOpen, rectRefreshToken, step?.anchorTarget, step?.padding, step?.target, step?.targetAll]);

    const completeMutation = useMutation({
        mutationFn: () => userMetadataApi.completeTutorialPage(config.pageKey, config.version, activeBusinessId || undefined),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userMetadata'] });
            queryClient.invalidateQueries({ queryKey: ['userMetadata', activeBusinessId] });
        },
    });

    const cardPosition = useMemo(() => {
        const tooltipWidth = Math.min(282, Math.max(240, window.innerWidth - 32));
        const tooltipCardWidth = Math.min(264, tooltipWidth);
        if (isMobile) {
            const mobileAnchorRect = step?.positionFromHighlight ? rect : anchorRect;
            const left = Math.min(
                Math.max(16, mobileAnchorRect.left + mobileAnchorRect.width / 2 - tooltipCardWidth / 2),
                Math.max(16, window.innerWidth - tooltipCardWidth - 16),
            );
            const preferBelow = mobileAnchorRect.top + mobileAnchorRect.height + 8;
            const top = preferBelow + 174 < window.innerHeight
                ? preferBelow
                : Math.max(62, mobileAnchorRect.top - 190);
            const arrowOffset = Math.min(
                Math.max(10, mobileAnchorRect.left + mobileAnchorRect.width / 2 - left - 12),
                tooltipCardWidth - 36,
            );
            return { left, top, width: tooltipCardWidth, arrowOffset };
        }
        const sideVerticalOffset = step?.sideOffsetY ?? -80;
        if (placement === 'right') {
            const isSidebarAnchor = step?.target?.includes('data-tutorial-nav');
            const rightArrowOffset = 20;
            const arrowHalfHeight = 12;
            const rightTop = isSidebarAnchor
                ? anchorRect.top + anchorRect.height / 2 - rightArrowOffset - arrowHalfHeight
                : step?.alignArrowToAnchor
                    ? anchorRect.top + anchorRect.height / 2 - rightArrowOffset - arrowHalfHeight
                : anchorRect.top + anchorRect.height / 2 - 42 + sideVerticalOffset;
            return {
                left: Math.min(anchorRect.left + anchorRect.width + 12 + (step?.sideOffsetX ?? 0), Math.max(16, window.innerWidth - tooltipWidth - 16)),
                top: Math.min(
                    Math.max(16, rightTop),
                    Math.max(16, window.innerHeight - 190),
                ),
                width: tooltipWidth,
                arrowOffset: rightArrowOffset,
            };
        }
        if (placement === 'left') {
            return {
                left: Math.max(16, anchorRect.left - tooltipWidth - 12 + (step?.sideOffsetX ?? 0)),
                top: Math.min(
                    Math.max(16, anchorRect.top + anchorRect.height / 2 - 42 + sideVerticalOffset),
                    Math.max(16, window.innerHeight - 190),
                ),
                width: tooltipWidth,
                arrowOffset: 20,
            };
        }

        if (placement === 'top') {
            const topAnchorRect = step?.positionFromHighlight ? rect : anchorRect;
            const topHorizontalOffset = step?.tooltipOffsetX ?? -60;
            const sidebarSafeLeft = 86;
            const left = Math.min(
                Math.max(sidebarSafeLeft, topAnchorRect.left + topAnchorRect.width / 2 - tooltipWidth / 2 + topHorizontalOffset),
                Math.max(16, window.innerWidth - tooltipWidth - 16),
            );
            const top = Math.max(16, topAnchorRect.top - 180 + (step?.sideOffsetY ?? 0));
            const topArrowHorizontalOffset = step?.arrowOffsetX ?? topHorizontalOffset;
            const arrowOffset = step?.arrowOffset ?? Math.min(
                Math.max(4, topAnchorRect.left + topAnchorRect.width / 2 - left - 12 + topArrowHorizontalOffset),
                tooltipWidth - 52,
            );
            return { left, top, width: tooltipWidth, arrowOffset };
        }

        const bottomAnchorRect = step?.positionFromHighlight ? rect : anchorRect;
        const bottomHorizontalOffset = step?.tooltipOffsetX ?? -60;
        const sidebarSafeLeft = 86;
        const left = Math.min(
            Math.max(sidebarSafeLeft, bottomAnchorRect.left + bottomAnchorRect.width / 2 - tooltipWidth / 2 + bottomHorizontalOffset),
            Math.max(16, window.innerWidth - tooltipWidth - 16),
        );
        const preferBelow = bottomAnchorRect.top + bottomAnchorRect.height + 6;
        const top = preferBelow + 174 < window.innerHeight
            ? preferBelow
            : Math.max(72, bottomAnchorRect.top - 190);
        const bottomArrowHorizontalOffset = step?.arrowOffsetX ?? -60;
        const arrowOffset = step?.arrowOffset ?? Math.min(
            Math.max(4, bottomAnchorRect.left + bottomAnchorRect.width / 2 - left - 12 + bottomArrowHorizontalOffset),
            tooltipWidth - 52,
        );
        return { left, top, width: tooltipWidth, arrowOffset };
    }, [anchorRect, isMobile, placement, rect, step?.alignArrowToAnchor, step?.arrowOffset, step?.arrowOffsetX, step?.positionFromHighlight, step?.sideOffsetX, step?.sideOffsetY, step?.target, step?.tooltipOffsetX]);

    const closeAndComplete = async () => {
        setIsOpen(false);
        window.dispatchEvent(new CustomEvent('finz:tutorial:closed', { detail: { pageKey: config.pageKey } }));
        if (completedVersion < config.version) {
            await completeMutation.mutateAsync();
        }
        onComplete?.();
    };

    const skipAndClose = () => {
        setIsOpen(false);
        window.dispatchEvent(new CustomEvent('finz:tutorial:closed', { detail: { pageKey: config.pageKey } }));
        onClose?.();
    };

    if (!isOpen || !step) return null;

    return (
        <div
            className="fixed inset-0 pointer-events-auto"
            style={{ zIndex: 4000 }}
        >
            {!shouldHighlight && step.dim !== false && <div className="absolute inset-0 bg-black/20" />}
            {shouldHighlight && (
                <div
                    className="absolute rounded-lg border-2 border-[#5B5DE4] bg-transparent"
                    style={{
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                    }}
                />
            )}
            <div
                className={`pointer-events-auto absolute text-[#1F2937] drop-shadow-[0_2px_12px_rgba(0,0,0,0.08)] ${effectivePlacement === 'right' || effectivePlacement === 'left' ? 'flex items-start' : 'flex flex-col items-start'}`}
                style={cardPosition}
            >
                {effectivePlacement === 'right' ? (
                    <div
                        className="-mr-1 h-0 w-0 flex-none border-y-[12px] border-r-[26px] border-y-transparent border-r-[#DDE8FF]"
                        style={{ marginTop: cardPosition.arrowOffset }}
                    />
                ) : effectivePlacement === 'left' ? (
                    <>
                        <div className="flex min-h-[174px] w-[264px] flex-col gap-5 rounded-xl bg-[#DDE8FF] p-5">
                            <div className="flex flex-col gap-1">
                                <div className="text-sm font-medium leading-[120%]">{step.title}</div>
                                <div className="text-xs font-normal leading-[140%] text-[#4C545F]">{step.body}</div>
                            </div>
                            <div className="mt-auto flex h-[25px] items-center justify-between gap-5">
                                <span className="text-[10px] font-medium leading-none text-[#989DA3]">
                                    {stepIndex + 1} of {config.steps.length}
                                </span>
                                <div className="flex items-center gap-2">
                                    {!isLastStep && (
                                        <button
                                            type="button"
                                            onClick={skipAndClose}
                                            className="inline-flex h-[25px] items-center justify-center rounded px-2 text-xs font-medium leading-[120%] text-[#4C545F] hover:bg-[#cbd9f5]"
                                        >
                                            Skip
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (isLastStep) {
                                                void closeAndComplete();
                                            } else {
                                                setStepIndex((current) => Math.min(current + 1, config.steps.length - 1));
                                            }
                                        }}
                                        className="inline-flex h-[25px] min-w-[51px] items-center justify-center rounded bg-[#1F2937] px-3 text-xs font-medium leading-[120%] text-white hover:bg-[#111827]"
                                    >
                                        {isLastStep ? 'End' : 'Next'}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 -ml-1 h-0 w-0 flex-none border-y-[12px] border-l-[26px] border-y-transparent border-l-[#DDE8FF]" />
                    </>
                ) : effectivePlacement === 'top' ? null : (
                    <div
                        className="-mb-1 h-0 w-0 flex-none border-x-[12px] border-b-[26px] border-x-transparent border-b-[#DDE8FF]"
                        style={{ marginLeft: cardPosition.arrowOffset }}
                    />
                )}
                {effectivePlacement === 'left' ? null : (
                    <div className="flex min-h-[174px] flex-col gap-5 rounded-xl bg-[#DDE8FF] p-5" style={{ width: Math.min(264, cardPosition.width) }}>
                        <div className="flex flex-col gap-1">
                            <div className="text-sm font-medium leading-[120%]">{step.title}</div>
                            <div className="text-xs font-normal leading-[140%] text-[#4C545F]">{step.body}</div>
                        </div>
                        <div className="mt-auto flex h-[25px] items-center justify-between gap-5">
                            <span className="text-[10px] font-medium leading-none text-[#989DA3]">
                                {stepIndex + 1} of {config.steps.length}
                            </span>
                            <div className="flex items-center gap-2">
                                {!isLastStep && (
                                    <button
                                        type="button"
                                        onClick={skipAndClose}
                                        className="inline-flex h-[25px] items-center justify-center rounded px-2 text-xs font-medium leading-[120%] text-[#4C545F] hover:bg-[#cbd9f5]"
                                    >
                                        Skip
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (isLastStep) {
                                            void closeAndComplete();
                                        } else {
                                            setStepIndex((current) => Math.min(current + 1, config.steps.length - 1));
                                        }
                                    }}
                                    className="inline-flex h-[25px] min-w-[51px] items-center justify-center rounded bg-[#1F2937] px-3 text-xs font-medium leading-[120%] text-white hover:bg-[#111827]"
                                >
                                    {isLastStep ? 'End' : 'Next'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {effectivePlacement === 'top' && (
                    <div
                        className="-mt-1 h-0 w-0 flex-none border-x-[12px] border-t-[26px] border-x-transparent border-t-[#DDE8FF]"
                        style={{ marginLeft: cardPosition.arrowOffset }}
                    />
                )}
            </div>
        </div>
    );
}
